import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, DateTools, Divider, Input, Patterns, RadioList, Select, Utils } from '@treezor/design-system';

import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';

import { Contact } from '@/features/user/components/CreateUser/Contact';
import { SirenInput } from '@/features/user/components/CreateUser/SirenInput';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { UserContentLabel } from '@/features/user/components/contentLayout';
import { legalForms, IUser, legalFormCountries } from '@/features/user/store/user.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { UserTools } from '@/utils/tools/UserTools';

import { useUpdateUser } from './useUpdateUser';

interface ISelfEmployedInformationProps {
  user: IUser;
  isMainInformationOnEdition: boolean;
  isAddressesOnEdition: boolean;
  isContactOnEdition: boolean;
}

export function useSelfEmployedInformationProps({
  user,
  isMainInformationOnEdition,
  isAddressesOnEdition,
  isContactOnEdition,
}: ISelfEmployedInformationProps) {
  const { t } = useTranslation();
  const config = useDashboardConfig();

  const [isSameAsBusinessAddress, setIsSameAsBusinessAddress] = useState<boolean>(
    user.country === user.secondaryCountry &&
      user.address1 === user.secondaryAddress1 &&
      user.address2 === user.secondaryAddress2 &&
      user.address3 === user.secondaryAddress3 &&
      user.postcode === user.secondaryPostcode &&
      user.postcode === user.secondaryPostcode &&
      user.city === user.secondaryCity,
  );

  const { countryCodeOptions, titleOptions, legalSectorTypeOptions, persoAndBusinessAddressOptions } =
    useFieldOptions();

  const {
    onUpdateUser,
    handleValueChange,
    register,
    handleInputChange,
    isLoading,
    watch,
    inputMaxLength,
    hasInvalidInputLength,
    handlePrefillData,
    handleDateChange,
    language,
    touchedFields,
    onCancelEdition,
  } = useUpdateUser(user);

  const distributionCountry = config?.settings?.users?.distributionCountry;
  const hasDistributionCountry = distributionCountry?.length > 0;

  const emailWatch = watch('email');
  const lastNameWatch = watch('lastname');
  const firstNameWatch = watch('firstname');
  const distributionCountryWatch = watch('distributionCountry');
  const shouldDisableUpdateDistributionCountry = legalFormCountries.includes(distributionCountryWatch);
  const legalRegistrationNumberWatch = watch('legalRegistrationNumber');
  const legalRegistrationDateWatch = watch('legalRegistrationDate');
  const selectedlegalRegistrationDate = DateTools.localStringToDate(legalRegistrationDateWatch, language);
  const legalSectorTypeWatch = watch('legalSectorType');
  const legalSectorWatch = watch('legalSector');

  const countryWatch = watch('country');
  const address1Watch = watch('address1');
  const address2Watch = watch('address2');
  const address3Watch = watch('address3');
  const postCodeWatch = watch('postcode');
  const cityWatch = watch('city');
  const secondaryCountryWatch = watch('secondaryCountry');
  const secondaryAddress1Watch = watch('secondaryAddress1');
  const secondaryAddress2Watch = watch('secondaryAddress2');
  const secondaryAddress3Watch = watch('secondaryAddress3');

  const phoneWatch = watch('phone');
  const mobileWatch = watch('mobile');

  const onChangeIsSameAsBusinessAddress = useCallback(
    (value: 'yes' | 'no') => {
      setIsSameAsBusinessAddress(value === 'yes');
      if (value === 'yes') {
        handleValueChange(countryWatch, 'secondaryCountry');
        handleValueChange(address1Watch, 'secondaryAddress1');
        handleValueChange(address2Watch, 'secondaryAddress2');
        handleValueChange(address3Watch, 'secondaryAddress3');
        handleValueChange(postCodeWatch, 'secondaryPostcode');
        handleValueChange(cityWatch, 'secondaryCity');
      }
    },
    [handleValueChange, countryWatch, address1Watch, address2Watch, address3Watch, postCodeWatch, cityWatch],
  );

  const {
    iconStatus,
    error,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch, isRequired: true });

  const emailError = useMemo(
    () => (user.email !== emailWatch || !emailWatch ? error : undefined),
    [emailWatch, error, user.email],
  );

  const isMainInformationInvalid = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      !firstNameWatch ||
      !lastNameWatch ||
      (hasDistributionCountry && !distributionCountryWatch) ||
      !legalRegistrationNumberWatch ||
      !UserTools.validateIntLegalNumber(legalRegistrationNumberWatch) ||
      (!!legalSectorWatch && !Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)),
    [
      emailWatch,
      emailError,
      firstNameWatch,
      lastNameWatch,
      hasDistributionCountry,
      distributionCountryWatch,
      legalRegistrationNumberWatch,
      legalSectorWatch,
      legalSectorTypeWatch,
    ],
  );

  const isAddressValid = useMemo(
    () =>
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      hasInvalidInputLength(['secondaryAddress1', 'secondaryAddress2', 'secondaryAddress3']),

    [hasInvalidInputLength],
  );

  const isContactInvalid = useMemo(
    () => (!!mobileWatch && !Patterns.phone.test(mobileWatch)) || (!!phoneWatch && !Patterns.phone.test(phoneWatch)),
    [mobileWatch, phoneWatch],
  );

  const mainInformation: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
      ...(isMainInformationOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-5 w-[600px]">
                <div className="flex flex-col gap-3">
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.title')}</UserContentLabel>
                    <Select
                      options={titleOptions}
                      defaultValue={user?.title}
                      onChange={(value: string) => handleValueChange(value, 'title')}
                      showDelete
                      size="s"
                      testId="select-kyc-title"
                    />
                  </CustomColumns>
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.lastname')}</UserContentLabel>
                    <Input
                      type="text"
                      label=""
                      required
                      size="s"
                      {...register('lastname')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'lastname')
                      }
                      testId="input-kyc-lastname"
                    />
                  </CustomColumns>
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.firstname')}</UserContentLabel>
                    <Input
                      type="text"
                      required
                      label=""
                      size="s"
                      {...register('firstname')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'firstname')
                      }
                      testId="input-kyc-firstname"
                    />
                  </CustomColumns>
                  <AdditonalDataLayout
                    shouldShow={true}
                    label={t('pages.dashboard.createUser.form.actions.addMiddleNames')}
                  >
                    <CustomColumns columns={[1, 3]}>
                      <UserContentLabel>{t('pages.dashboard.createUser.form.middleName')}</UserContentLabel>
                      <CustomColumns columns={[1, 1]} className="gap-3">
                        <Input
                          type="text"
                          label=""
                          size="s"
                          {...register('middleName1')}
                          onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            handleInputChange(event, 'middleName1')
                          }
                          testId="input-kyc-middlename-1"
                        />
                        <Input
                          type="text"
                          label=""
                          size="s"
                          {...register('middleName2')}
                          onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            handleInputChange(event, 'middleName2')
                          }
                          testId="input-kyc-middlename-2"
                        />
                      </CustomColumns>
                    </CustomColumns>
                  </AdditonalDataLayout>
                  <CustomColumns columns={[1, 3]}>
                    <UserContentLabel>{t('pages.dashboard.createUser.form.email')}</UserContentLabel>
                    <Input
                      type="text"
                      icon={{ left: 'mail', right: iconStatus }}
                      label=""
                      required
                      {...register('email', { pattern: Patterns.email })}
                      error={emailError}
                      onBlur={handleEmailBlur}
                      size="s"
                      testId="input-kyc-email"
                    />
                  </CustomColumns>
                  {hasDistributionCountry && (
                    <CustomColumns columns={[1, 3]}>
                      <UserContentLabel>
                        {t('pages.currentProfiles.profile.informations.distributionCountry')}
                      </UserContentLabel>
                      <Select
                        required
                        withSearch
                        disabled={shouldDisableUpdateDistributionCountry}
                        options={countryCodeOptions.filter(({ value }) => {
                          const allowedCountries = !shouldDisableUpdateDistributionCountry
                            ? distributionCountry.filter((country) => !legalFormCountries.includes(country))
                            : distributionCountry;
                          return allowedCountries.includes(value);
                        })}
                        defaultValue={user?.distributionCountry}
                        noOptionsComponent={<CreateUserEmptyComponent />}
                        size="s"
                        onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
                        testId="select-kyc-distribution-country"
                      />
                    </CustomColumns>
                  )}
                </div>
                <Divider thickness="200" key={`section-divider-information`} />
                <SirenInput
                  required
                  value={legalRegistrationNumberWatch}
                  hasEntered={true}
                  label={t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber')}
                  handlePrefillData={handlePrefillData}
                  {...register('legalRegistrationNumber')}
                  size="s"
                  testId="input-kyc-legal-registration-number"
                >
                  <div className="flex flex-col gap-3">
                    <Input
                      {...register('legalName')}
                      type="text"
                      label={t('pages.dashboard.createUser.form.legalName')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'legalName')
                      }
                      size="s"
                      testId="input-kyc-legal-name"
                    />
                    <CustomColumns columns={[1, 1]}>
                      <DatePicker
                        selectedDate={selectedlegalRegistrationDate}
                        label={t('pages.dashboard.createUser.form.legalRegistrationDate')}
                        todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                        clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                        onChangeDate={(value) => handleDateChange(value, 'legalRegistrationDate')}
                        language={language}
                        size="s"
                        testId="date-picker-kyc-legal-registration"
                      />
                      <Input
                        type="text"
                        {...register('legalTvaNumber')}
                        label={t('pages.dashboard.createUser.form.legalTvaNumber')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                          handleInputChange(event, 'legalTvaNumber');
                        }}
                        size="s"
                        testId="input-kyc-legal-tva-number"
                      />
                    </CustomColumns>
                  </div>
                </SirenInput>
                <Divider thickness="200" key={`section-divider-siren`} />
                <CustomColumns columns={[1, 1]}>
                  <RadioList
                    label={t('pages.dashboard.createUser.form.legalSectorType')}
                    options={legalSectorTypeOptions}
                    value={legalSectorTypeWatch}
                    onChange={(value: string) => handleValueChange(value, 'legalSectorType')}
                    className="pt-2 gap-3"
                    isRequired
                  />
                  <Input
                    type="text"
                    label={t('pages.dashboard.createUser.form.legalSectorNafApe')}
                    {...register('legalSector')}
                    error={
                      legalSectorWatch === '' || Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)
                        ? undefined
                        : legalSectorTypeWatch === 'NACE'
                          ? t('pages.dashboard.createUser.form.rules.incorrectNaceLegalSector')
                          : t('pages.dashboard.createUser.form.rules.incorrectLegalSector')
                    }
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'legalSector')
                    }
                    size="s"
                    testId="input-kyc-legal-sector"
                  />
                </CustomColumns>
              </div>
            ),
          }
        : {
            categories: [
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.title'),
                    value: user.title ? t(`pages.dashboard.users.titles.${user.title}`) : '/',
                  },
                  { label: t('pages.dashboard.createUser.form.lastname'), value: user.lastname },
                  {
                    label: t('pages.dashboard.createUser.form.firstname'),
                    value: [user.firstname, ...user.middleNames.split(', ')].filter(Boolean).join(', '),
                  },
                  { label: t('pages.dashboard.createUser.form.email'), value: user.email },
                  distributionCountry?.length > 0 && {
                    label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                    value: t(`shared.countries.${user.distributionCountry}`),
                  },
                ],
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber'),
                    value: user?.legalRegistrationNumber,
                  },
                  { label: t('pages.dashboard.createUser.form.legalName'), value: user?.legalName || '/' },
                  {
                    label: t('pages.dashboard.createUser.form.legalRegistrationDate'),
                    value: user?.legalRegistrationDate || '/',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalForm'),
                    value: legalForms?.[user?.legalForm] || '/',
                  },
                  { label: t('pages.dashboard.createUser.form.legalTvaNumber'), value: user?.legalTvaNumber || '/' },
                ],
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalSector'),
                    value: `${user?.legalSectorType} - ${user?.legalSector}`,
                  },
                ],
              },
            ],
          }),
    }),
    [
      countryCodeOptions,
      distributionCountry,
      emailError,
      handleDateChange,
      handleEmailBlur,
      handleInputChange,
      handlePrefillData,
      handleValueChange,
      iconStatus,
      isMainInformationOnEdition,
      language,
      legalRegistrationNumberWatch,
      legalSectorTypeOptions,
      legalSectorTypeWatch,
      legalSectorWatch,
      register,
      selectedlegalRegistrationDate,
      t,
      titleOptions,
      hasDistributionCountry,
      user.distributionCountry,
      user.email,
      user.firstname,
      user.lastname,
      user?.legalForm,
      user?.legalName,
      user?.legalRegistrationDate,
      user?.legalRegistrationNumber,
      user?.legalSector,
      user?.legalSectorType,
      user?.legalTvaNumber,
      user.middleNames,
      user?.title,
      shouldDisableUpdateDistributionCountry,
    ],
  );

  const address: TKycSection = useMemo(
    () => ({
      subTitle: t('pages.dashboard.createUser.form.blocks.address'),
      ...(isAddressesOnEdition
        ? {
            component: (
              <div className="flex flex-col gap-4">
                <Select
                  label={t('pages.currentProfiles.profile.contact.country')}
                  options={countryCodeOptions}
                  withSearch
                  defaultValue={countryWatch}
                  onChange={(value: string) => {
                    handleValueChange(value, 'country');
                  }}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  showDelete
                  size="s"
                  testId="select-kyc-country"
                />

                <CustomColumns columns={[1, 1]}>
                  <Input
                    value={address1Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address1')}
                    {...register('address1')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address1')
                    }
                    {...inputMaxLength(address1Watch, 35)}
                    size="s"
                    testId="input-kyc-address1"
                  />

                  <Input
                    value={address2Watch}
                    type="text"
                    label={t('pages.dashboard.createUser.form.address2')}
                    {...register('address2')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'address2')
                    }
                    {...inputMaxLength(address2Watch, 35)}
                    size="s"
                    testId="input-kyc-address2"
                  />
                </CustomColumns>

                <Input
                  value={address3Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address3')}
                  {...register('address3')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'address3')
                  }
                  {...inputMaxLength(address3Watch, 35)}
                  size="s"
                  testId='input-kyc-address"'
                />
                <CustomColumns columns={[1, 1]}>
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.postcode')}
                    {...register('postcode')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'postcode')
                    }
                    size="s"
                    testId="input-kyc-postcode"
                  />
                  <Input
                    type="text"
                    label={t('pages.currentProfiles.profile.contact.city')}
                    {...register('city')}
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'city')
                    }
                    size="s"
                    testId="input-kyc-city"
                  />
                </CustomColumns>

                <RadioList
                  name="isSameAsBusinessAddress"
                  options={persoAndBusinessAddressOptions}
                  value={isSameAsBusinessAddress ? 'yes' : 'no'}
                  onChange={onChangeIsSameAsBusinessAddress}
                  className="pt-2 gap-3"
                  direction="row"
                />

                {!isSameAsBusinessAddress && (
                  <>
                    <Select
                      label={t('pages.currentProfiles.profile.contact.country')}
                      options={countryCodeOptions}
                      withSearch
                      defaultValue={secondaryCountryWatch}
                      onChange={(value: string) => handleValueChange(value, 'secondaryCountry')}
                      noOptionsComponent={<CreateUserEmptyComponent />}
                      showDelete
                      size="s"
                      testId="select-kyc-secondary-country"
                    />
                    <CustomColumns columns={[1, 1]}>
                      <Input
                        value={secondaryAddress1Watch}
                        type="text"
                        label={t('pages.dashboard.createUser.form.address1')}
                        {...register('secondaryAddress1')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'secondaryAddress1')
                        }
                        {...inputMaxLength(secondaryAddress1Watch, 35)}
                        size="s"
                        testId="input-kyc-secondary-address1"
                      />

                      <Input
                        value={secondaryAddress2Watch}
                        type="text"
                        label={t('pages.dashboard.createUser.form.address2')}
                        {...register('secondaryAddress2')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'secondaryAddress2')
                        }
                        {...inputMaxLength(secondaryAddress2Watch, 35)}
                        size="s"
                        testId="input-kyc-secondary-address2"
                      />
                    </CustomColumns>

                    <Input
                      value={secondaryAddress3Watch}
                      type="text"
                      label={t('pages.dashboard.createUser.form.address3')}
                      {...register('secondaryAddress3')}
                      onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleInputChange(event, 'secondaryAddress3')
                      }
                      {...inputMaxLength(secondaryAddress3Watch, 35)}
                      size="s"
                      testId="input-kyc-secondary-address3"
                    />
                    <CustomColumns columns={[1, 1]}>
                      <Input
                        type="text"
                        label={t('pages.currentProfiles.profile.contact.postcode')}
                        {...register('secondaryPostcode')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'secondaryPostcode')
                        }
                        size="s"
                        testId="input-kyc-secondary-postcode"
                      />
                      <Input
                        type="text"
                        label={t('pages.currentProfiles.profile.contact.city')}
                        {...register('secondaryCity')}
                        onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                          handleInputChange(event, 'secondaryCity')
                        }
                        size="s"
                        testId="input-kyc-secondary-city"
                      />
                    </CustomColumns>
                  </>
                )}
              </div>
            ),
          }
        : {
            categories: [
              {
                component: (
                  <div className="flex gap-8">
                    <AddressSummary
                      isPrimary
                      title={t('pages.dashboard.createUser.selfEmployed.sections.professionalAddress.title')}
                      address1={user.address1}
                      address2={user.address2}
                      address3={user.address3}
                      postcode={user.postcode}
                      city={user.city}
                      country={user.country}
                    />

                    <AddressSummary
                      title={t('pages.dashboard.createUser.selfEmployed.sections.personnalAddress')}
                      address1={user.secondaryAddress1}
                      address2={user.secondaryAddress2}
                      address3={user.secondaryAddress3}
                      postcode={user.secondaryPostcode}
                      city={user.secondaryCity}
                      country={user.secondaryCountry}
                    />
                  </div>
                ),
              },
            ],
          }),
    }),
    [
      address1Watch,
      address2Watch,
      address3Watch,
      countryCodeOptions,
      countryWatch,
      handleInputChange,
      handleValueChange,
      inputMaxLength,
      isAddressesOnEdition,
      isSameAsBusinessAddress,
      onChangeIsSameAsBusinessAddress,
      persoAndBusinessAddressOptions,
      register,
      secondaryAddress1Watch,
      secondaryAddress2Watch,
      secondaryAddress3Watch,
      secondaryCountryWatch,
      t,
      user.address1,
      user.address2,
      user.address3,
      user.city,
      user.country,
      user.postcode,
      user.secondaryAddress1,
      user.secondaryAddress2,
      user.secondaryAddress3,
      user.secondaryCity,
      user.secondaryCountry,
      user.secondaryPostcode,
    ],
  );

  const contact: TKycSection = {
    subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
    ...(isContactOnEdition
      ? {
          component: (
            <div className="flex flex-col gap-3">
              <CustomColumns columns={[1, 3]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.phone')}</UserContentLabel>
                <Contact
                  label=""
                  value={phoneWatch}
                  initialValue={user?.phone}
                  initialCountry={!touchedFields?.['phone'] && !phoneWatch ? distributionCountryWatch : undefined}
                  handleValueChange={handleValueChange}
                  size="s"
                  {...register('phone')}
                  testId="input-kyc-phone"
                />
              </CustomColumns>
              <CustomColumns columns={[1, 3]}>
                <UserContentLabel>{t('pages.dashboard.createUser.form.mobile')}</UserContentLabel>
                <Contact
                  label=""
                  value={mobileWatch}
                  initialValue={user?.mobile}
                  initialCountry={!touchedFields?.['mobile'] && !mobileWatch ? distributionCountryWatch : undefined}
                  handleValueChange={handleValueChange}
                  size="s"
                  {...register('mobile')}
                  testId="input-kyc-mobile"
                />
              </CustomColumns>
            </div>
          ),
        }
      : {
          categories: [
            {
              items: [
                { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '/' },
                { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '/' },
              ],
            },
          ],
        }),
  };

  return {
    mainInformation,
    address,
    isLoading,
    isAddressValid,
    isContactInvalid,
    contact,
    onUpdateUser,
    onCancelEdition,
    isMainInformationInvalid,
  };
}
